import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { formatCurrency } from 'utils/formatters';
import { PAYMENT_MESSAGES, INFO_DIALOG_STYLES } from '../constants';

const TABULAR_NUMS_SX = { fontFeatureSettings: '"tnum"' };

const computeDelta = (currentTotal, paymentTotal) => {
  const current = typeof currentTotal === 'string' ? parseFloat(currentTotal) : currentTotal;
  const payment = typeof paymentTotal === 'string' ? parseFloat(paymentTotal) : paymentTotal;

  if (!Number.isFinite(current) || !Number.isFinite(payment) || current === payment) {
    return null;
  }

  const isIncrease = current > payment;
  const diff = current - payment;
  return {
    formatted: formatCurrency(Math.abs(diff), 'USD'),
    label: isIncrease ? 'increase' : 'decrease',
    color: isIncrease ? INFO_DIALOG_STYLES.DELTA_INCREASE_COLOR : INFO_DIALOG_STYLES.DELTA_DECREASE_COLOR,
    bgColor: isIncrease ? INFO_DIALOG_STYLES.DELTA_INCREASE_BG : INFO_DIALOG_STYLES.DELTA_DECREASE_BG,
    Icon: isIncrease ? ArrowUpwardIcon : ArrowDownwardIcon,
  };
};

/**
 * Dialog shown when the invoice total has changed since payments were last calculated.
 * Uses an info/blue tone — this is an advisory prompt, not a warning.
 *
 * @param {boolean} open - Whether the dialog is visible
 * @param {function} onDismiss - Dismiss handler (closes dialog without action)
 * @param {function} onRecalculate - Recalculate handler (triggers recalculation)
 * @param {boolean} isLoading - Whether the recalculation is in progress
 * @param {number|string} currentTotal - Current invoice total
 * @param {number|string} paymentTotal - Sum of current template payment values
 */
const StalePaymentDialog = ({ open, onDismiss, onRecalculate, isLoading, currentTotal, paymentTotal }) => {
  const delta = computeDelta(currentTotal, paymentTotal);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (isLoading) return;
        onDismiss();
      }}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: '12px', overflow: 'hidden' },
      }}
    >
      {/* Header */}
      <Box sx={{ px: 3, py: 2.5, bgcolor: INFO_DIALOG_STYLES.HEADER_BG, borderBottom: `1px solid ${INFO_DIALOG_STYLES.HEADER_BORDER}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoOutlinedIcon sx={{ color: INFO_DIALOG_STYLES.ICON_COLOR, fontSize: 22 }} />
          <Typography variant="subtitle1" fontWeight={600} color="text.primary">
            {PAYMENT_MESSAGES.STALE_DIALOG_TITLE}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {PAYMENT_MESSAGES.STALE_DIALOG_SUBTITLE}
        </Typography>
      </Box>

      <DialogContent sx={{ px: 3, py: 3 }}>
        {/* Side-by-side comparison */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Previous */}
          <Box
            sx={{
              flex: 1, textAlign: 'center', p: 2,
              border: '1px solid', borderColor: 'grey.200',
              borderRadius: '10px', bgcolor: 'grey.50',
            }}
          >
            <Typography
              variant="caption" color="text.secondary" fontWeight={500}
              sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.65rem' }}
            >
              Payment Total
            </Typography>
            <Typography
              variant="h6" fontWeight={500} color="text.secondary"
              sx={{ mt: 0.5, ...TABULAR_NUMS_SX }}
            >
              {formatCurrency(paymentTotal, 'USD')}
            </Typography>
          </Box>

          <ArrowForwardIcon sx={{ color: 'grey.400', fontSize: 20, flexShrink: 0 }} />

          {/* Current */}
          <Box
            sx={{
              flex: 1, textAlign: 'center', p: 2,
              border: `1px solid ${INFO_DIALOG_STYLES.CARD_BORDER}`,
              borderRadius: '10px', bgcolor: INFO_DIALOG_STYLES.CARD_BG,
            }}
          >
            <Typography
              variant="caption" color="text.secondary" fontWeight={500}
              sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.65rem' }}
            >
              Invoice Total
            </Typography>
            <Typography
              variant="h6" fontWeight={600} color="text.primary"
              sx={{ mt: 0.5, ...TABULAR_NUMS_SX }}
            >
              {formatCurrency(currentTotal, 'USD')}
            </Typography>
          </Box>
        </Box>

        {/* Delta chip */}
        {delta && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Box
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.5,
                bgcolor: delta.bgColor, color: delta.color,
                px: 1.5, py: 0.5, borderRadius: '16px',
              }}
            >
              <delta.Icon sx={{ fontSize: 14 }} />
              <Typography variant="body2" fontWeight={600} sx={TABULAR_NUMS_SX}>
                {delta.formatted}
              </Typography>
              <Typography variant="caption" fontWeight={500} sx={{ opacity: 0.8 }}>
                {delta.label}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: INFO_DIALOG_STYLES.FOOTER_BG }}>
        <Button
          onClick={onDismiss}
          variant="text"
          color="inherit"
          disabled={isLoading}
          sx={{ color: 'text.secondary', textTransform: 'none', fontWeight: 500 }}
        >
          {PAYMENT_MESSAGES.STALE_DIALOG_DISMISS}
        </Button>
        <Button
          onClick={onRecalculate}
          variant="contained"
          disabled={isLoading}
          startIcon={
            isLoading
              ? <CircularProgress size={16} color="inherit" />
              : <AutorenewIcon sx={{ fontSize: 18 }} />
          }
          sx={{
            textTransform: 'none', fontWeight: 600,
            borderRadius: '8px', px: 2.5,
          }}
        >
          {isLoading ? PAYMENT_MESSAGES.PROCESSING : PAYMENT_MESSAGES.STALE_DIALOG_CONFIRM}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StalePaymentDialog;
